import { FundingNeedResponse } from '@/models/FundingNeeds'
import {
  ListItem,
  ListItemText,
  Button,
  Typography,
  CircularProgress,
  LinearProgress,
  Alert,
  Box,
  Chip,
} from '@mui/material'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useScanTask } from '@/utils/tasks/scanTask'
import { useAdoptActiveScan } from '@/utils/tasks/useAdoptActiveScan'
import { useFundingNeedStaleness } from '@/utils/hooks/useFundingNeedStaleness'
import {
  ScanStatusAnnouncer,
  scanAnnouncement,
} from '../ScanStatusAnnouncer'
import { ScanBlockedTooltip } from '../ScanBlockedTooltip'
import UpgradeCTA from '../UpgradeCTA'

interface MatchNeedsListItemProps {
  token: string
  fundingNeed: FundingNeedResponse
  scanIds: number[] | undefined
  runScanOnAll: boolean
  isAnnualSubscriber?: boolean
}

export default function MobileMatchNeedsListItem({
  token,
  fundingNeed,
  scanIds,
  runScanOnAll,
}: MatchNeedsListItemProps) {
  const includeRelational = true
  const [isStartingScan, setIsStartingScan] = useState(false)
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false)
  const [upgradeReason, setUpgradeReason] = useState('')

  getCurrentUser() // Not using any values from this hook

  const {
    hasResults,
    canStartScan,
    scanBlockedReason,
    scanBlockedScope,
    viewResultsDisabled,
    showStaleWarning,
    warningMessage,
    latestScanFoundNothing,
  } = useFundingNeedStaleness(token, fundingNeed, scanIds)

  const needId = fundingNeed.id ? String(fundingNeed.id) : ''

  // Without this the row only knows about scans this browser started: one started
  // on another device, by a colleague or during onboarding renders as an idle
  // "Re-find Matches" button that POSTs straight into a 409.
  useAdoptActiveScan(token as string, Number(fundingNeed.id))

  // Shared task hook rather than a direct runScan call: this is what gives the
  // row real progress, picks up scans started in another tab or by a colleague,
  // and routes profile-regen 409s to the block dialog.
  const {
    startScan,
    isLoadingForNeed,
    progressForNeed,
    latestStatusForNeed,
    hasActiveScan,
    errorForNeed,
    isScanStaleForNeed,
    refreshScanState,
  } = useScanTask({
    token,
    // Fires for every scan that finishes, not just this row's.
    onCompleted: (_results, _taskId, completedNeedId) => {
      if (completedNeedId === String(fundingNeed.id)) setIsStartingScan(false)
    },
    // Fires for every scan that fails, not just this row's.
    onError: (error, _taskId, erroredNeedId) => {
      if (erroredNeedId !== String(fundingNeed.id)) return
      setIsStartingScan(false)
      if (error.name === 'PaymentRequired') {
        setUpgradeReason(error.message)
        setShowUpgradeDialog(true)
      }
    },
  })

  useEffect(() => {
    refreshScanState()
  }, [refreshScanState])

  const isStale = needId ? isScanStaleForNeed(needId) : false
  const isScanning = needId
    ? (hasActiveScan(needId) || isLoadingForNeed(needId) || isStartingScan) &&
      !isStale
    : false
  const scanProgress = needId ? progressForNeed(needId) || 0 : 0
  const isScanComplete = needId
    ? latestStatusForNeed(needId) === 'completed'
    : false
  const scanError = needId ? errorForNeed(needId) : null

  async function handleRunScan(includeFuturePlanning = false) {
    if (!token || !needId) return
    // The scan buttons are disabled with this reason; this also stops a
    // click on the span-rendered buttons, which `disabled` does not.
    if (scanBlockedReason) return

    try {
      setIsStartingScan(true)
      const jobId = await startScan(
        needId,
        includeRelational,
        includeFuturePlanning,
      )
      if (!jobId) {
        setIsStartingScan(false)
      }
    } catch {
      setIsStartingScan(false)
    }
  }

  useEffect(() => {
    if (runScanOnAll) {
      handleRunScan()
    }
  }, [runScanOnAll])

  const actionButtonSx = {
    borderRadius: 2,
    textTransform: 'none',
    fontSize: '16px',
    flexGrow: 1,
    maxWidth: '150px',
  }

  return (
    <ListItem
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        padding: 2,
      }}
    >
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {fundingNeed.title}
            {fundingNeed.is_archived && (
              <Chip
                label="Archived"
                size="small"
                sx={{
                  ml: 1,
                  height: '20px',
                  fontSize: '11px',
                  bgcolor: 'grey.200',
                  color: 'grey.700',
                }}
              />
            )}
          </Box>
        }
      />

      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}
      >
        {/* Desktop puts this on a hover tooltip; touch has no hover, so the
            message is spelled out inline. */}
        {hasResults && showStaleWarning && !isScanning && warningMessage && (
          <Alert severity="warning" sx={{ width: '100%', fontSize: '14px' }}>
            {warningMessage}
          </Alert>
        )}
        {/* Mobile has no hover, so the reason is inline. Account-wide blocks are
            left to the page banner rather than repeated on every row. */}
        {scanBlockedReason && scanBlockedScope === 'request' && !isScanning && (
          <Alert severity="info" sx={{ width: '100%', fontSize: '14px' }}>
            {scanBlockedReason}
          </Alert>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-evenly',
            alignItems: 'center',
            mt: 2,
            width: '100%',
            gap: 1,
          }}
        >
          {/* Disabled rather than hidden for stale results so a re-scan comes
              first. Stays enabled when the org can't currently re-scan
              (paused/at cap) — then it's their only way back to their matches. */}
          {hasResults && (
            <Button
              variant="outlined"
              disableRipple
              disabled={viewResultsDisabled}
              {...(viewResultsDisabled
                ? {}
                : { component: Link, to: `/match/${fundingNeed.id}` })}
              aria-label={
                viewResultsDisabled
                  ? `View results for ${fundingNeed.title} (disabled — results are out of date)`
                  : `View results for ${fundingNeed.title}`
              }
              sx={{
                ...actionButtonSx,
                color: '#1C3045',
                borderColor: '#1C3045',
                border: 2,
                '&:focus': {
                  backgroundColor: '#e8e8e8',
                },
              }}
            >
              View Results
            </Button>
          )}

          {isScanning ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                maxWidth: '150px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#1C3045',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress size={20} sx={{ color: '#1C3045', mr: 1 }} />
                Matching...
              </Typography>
              <LinearProgress
                variant="determinate"
                value={scanProgress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  mt: 0.5,
                  bgcolor: '#E0E0E0',
                  '& .MuiLinearProgress-bar': { bgcolor: '#1C3045' },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  color: '#666',
                  mt: 0.25,
                }}
              >
                {scanProgress}% complete
              </Typography>
            </Box>
          ) : scanError ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                maxWidth: '150px',
              }}
            >
              <Alert severity="error" sx={{ mb: 0.5 }}>
                Sorry, something went wrong!
              </Alert>
              <ScanBlockedTooltip reason={scanBlockedReason}>
                <Button
                  variant="contained"
                  disableRipple
                  aria-label={`Try scanning again for ${fundingNeed.title}`}
                  disabled={!canStartScan}
                  onClick={() => handleRunScan()}
                  sx={{
                    ...actionButtonSx,
                    color: 'white',
                    bgcolor: '#1C3045',
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: '#162433' },
                  }}
                >
                  Try Again
                </Button>
              </ScanBlockedTooltip>
            </Box>
          ) : hasResults && showStaleWarning ? (
            <ScanBlockedTooltip reason={scanBlockedReason}>
              <Button
                variant="contained"
                disableRipple
                aria-label={`Re-find matches for ${fundingNeed.title}`}
                disabled={!canStartScan}
                onClick={() => handleRunScan()}
                sx={{
                  ...actionButtonSx,
                  color: 'white',
                  bgcolor: '#1C3045',
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: '#162433' },
                }}
              >
                Re-find Matches
              </Button>
            </ScanBlockedTooltip>
          ) : latestScanFoundNothing && !showStaleWarning ? (
            <Typography
              sx={{
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                color: '#1C3045',
                justifyContent: 'center',
                flexGrow: 1,
                // Wider than "Scan Complete"; the 150px cap wrapped it.
                whiteSpace: 'nowrap',
              }}
            >
              <InfoOutlinedIcon
                sx={{ color: '#1C3045', mr: 1, fontSize: '20px' }}
              />
              No matches found
            </Typography>
          ) : hasResults || (isScanComplete && !showStaleWarning) ? (
            // A just-finished scan reads as complete only while the request is still
            // current; once it's edited or goes stale the scan button comes back.
            <Typography
              sx={{
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                color: '#1C3045',
                justifyContent: 'center',
                flexGrow: 1,
                maxWidth: '150px',
              }}
            >
              <TaskAltIcon sx={{ color: '#1C3045', mr: 1, fontSize: '20px' }} />
              Scan Complete
            </Typography>
          ) : (
            <ScanBlockedTooltip reason={scanBlockedReason}>
              <Button
                variant="contained"
                disableRipple
                aria-label={`Find matches for ${fundingNeed.title}`}
                disabled={!canStartScan}
                onClick={() => handleRunScan()}
                sx={{
                  ...actionButtonSx,
                  color: 'white',
                  bgcolor: '#1C3045',
                  borderColor: '#1C3045',
                  '&:hover': { backgroundColor: '#162433' },
                }}
              >
                Find Matches
              </Button>
            </ScanBlockedTooltip>
          )}
        </Box>
      </Box>

      {/* Upgrade dialog for 402 errors */}
      <ScanStatusAnnouncer
        message={scanAnnouncement({
          title: fundingNeed.title,
          isScanning,
          scanError,
          latestStatus: needId ? latestStatusForNeed(needId) : 'idle',
          latestScanFoundNothing,
        })}
      />

      <UpgradeCTA
        variant="dialog"
        isOpen={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        message={
          upgradeReason ||
          'Free tier users are limited to 2 scans. Please upgrade to continue.'
        }
      />
    </ListItem>
  )
}
